import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './styles.css';

const API_BASE = 'https://starfolio-worker.maggiecoronel8.workers.dev';

// Types
interface User {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

interface Product {
  id: string;
  type: 'wishlist' | 'digital' | 'link';
  title: string;
  description: string;
  price_stars: number;
  target_amount?: number;
  current_amount?: number;
  thumbnail_url?: string;
  resource_url?: string;
}

interface Profile {
  tenant_id: string;
  display_name: string;
  bio: string;
  avatar_url?: string;
  theme_id: string;
  social_links: Array<{ platform: string; url: string }>;
  bot_username?: string;
}

interface Purchase {
  id: string;
  title: string;
  type: string;
  resource_url?: string;
  thumbnail_url?: string;
  amount_stars: number;
  paid_at: number;
}

declare global {
  interface Window {
    Telegram: {
      WebApp: {
        initData: string;
        initDataUnsafe: { user?: User; start_param?: string };
        themeParams: any;
        colorScheme: 'light' | 'dark';
        expand(): void;
        ready(): void;
        close(): void;
        setHeaderColor(color: string): void;
        setBackgroundColor(color: string): void;
        setBottomBarColor(color: string): void;
        HapticFeedback: {
          impactOccurred(style: string): void;
          notificationOccurred(type: string): void;
          selectionChanged(): void;
        };
        MainButton: any;
        showPopup(params: any): void;
        showConfirm(message: string, callback: (ok: boolean) => void): void;
        openInvoice(url: string, callback?: (status: string) => void): void;
      };
    };
  }
}

function App() {
  const tg = window.Telegram?.WebApp;
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'products' | 'purchases'>('products');
  const [error, setError] = useState<string | null>(null);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [tenantId, setTenantId] = useState<string>('');

  useEffect(() => {
    if (tg) {
      tg.ready();
      tg.expand();

      // Apply Telegram theme to CSS variables
      const tp = tg.themeParams;
      if (tp) {
        const root = document.documentElement;
        if (tp.bg_color) root.style.setProperty('--bg-primary', tp.bg_color);
        if (tp.secondary_bg_color) root.style.setProperty('--bg-secondary', tp.secondary_bg_color);
        if (tp.section_bg_color) root.style.setProperty('--bg-card', tp.section_bg_color);
        if (tp.text_color) root.style.setProperty('--text-primary', tp.text_color);
        if (tp.hint_color) root.style.setProperty('--text-secondary', tp.hint_color);
        if (tp.subtitle_text_color) root.style.setProperty('--text-muted', tp.subtitle_text_color);
        if (tp.link_color) root.style.setProperty('--accent-primary', tp.link_color);
        try {
          tg.setHeaderColor(tp.bg_color || (tg.colorScheme === 'dark' ? '#0a0a0a' : '#ffffff'));
          tg.setBackgroundColor(tp.bg_color || (tg.colorScheme === 'dark' ? '#0a0a0a' : '#ffffff'));
        } catch (e) { /* older versions */ }
      }

      // Get tenant ID from multiple sources
      const hashId = window.location.hash?.replace('#', '') || '';
      const urlParams = new URLSearchParams(window.location.search);
      const tid = tg.initDataUnsafe?.start_param || hashId || urlParams.get('startapp') || urlParams.get('tid') || '';

      if (!tid) {
        setError('No profile specified. Please open this app from a bot link.');
        setLoading(false);
        return;
      }

      setTenantId(tid);
      loadData(tid);
    } else {
      setError('Please open this app from Telegram');
      setLoading(false);
    }
  }, []);

  const loadData = async (tid: string) => {
    try {
      const [profileRes, productsRes] = await Promise.all([
        fetch(`${API_BASE}/api/profile/${tid}`),
        fetch(`${API_BASE}/api/products/${tid}`),
      ]);

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setProfile(profileData);
      }

      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProducts(productsData);
      }
    } catch (e) {
      console.error('Failed to load data:', e);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadPurchases = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/my-purchases`, {
        headers: { 'Authorization': `tma ${tg.initData}`, 'X-Tenant-ID': tenantId }
      });

      if (res.ok) {
        const data = await res.json();
        setPurchases(data);
      }
    } catch (e) {
      console.error('Failed to load purchases:', e);
    }
  };

  const handlePurchase = async (product: Product) => {
    tg.HapticFeedback.impactOccurred('light');
    setPurchasing(product.id);

    try {
      const res = await fetch(`${API_BASE}/api/invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `tma ${tg.initData}`,
          'X-Tenant-ID': tenantId,
        },
        body: JSON.stringify({ productId: product.id }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to create invoice');
      }

      tg.openInvoice(data.invoiceUrl, (status) => {
        if (status === 'paid') {
          tg.HapticFeedback.notificationOccurred('success');
          loadData(tenantId);
          setActiveTab('purchases');
          loadPurchases();
        } else if (status === 'cancelled') {
          tg.HapticFeedback.notificationOccurred('warning');
        }
        setPurchasing(null);
      });
    } catch (err: any) {
      setError(err.message);
      tg.HapticFeedback.notificationOccurred('error');
      setPurchasing(null);
    }
  };

  const isDark = tg?.colorScheme === 'dark';

  if (loading) {
    return (
      <div className={`app ${isDark ? 'dark' : 'light'}`}>
        <div className="skeleton skeleton-header"></div>
        <div className="skeleton skeleton-avatar"></div>
        <div className="skeleton skeleton-text"></div>
        <div className="skeleton skeleton-text short"></div>
        <div style={{ display: 'flex', gap: '8px', padding: '12px 20px 0' }}>
          <div className="skeleton" style={{ flex: 1, height: 46, borderRadius: 14 }}></div>
          <div className="skeleton" style={{ flex: 1, height: 46, borderRadius: 14 }}></div>
        </div>
        <div className="skeleton skeleton-card"></div>
        <div className="skeleton skeleton-card"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="empty-state-screen">
        <div className="empty-icon">🌟</div>
        <h2>Welcome to StarFolio</h2>
        <p>This profile doesn't exist yet</p>
      </div>
    );
  }

  return (
    <div className={`app ${isDark ? 'dark' : 'light'}`}>
      {/* Header with Profile */}
      <header className="profile-header">
        <div className="header-bg" style={{
          background: profile.theme_id === 'ocean' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
            profile.theme_id === 'sunset' ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' :
              profile.theme_id === 'neon' ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' :
                profile.theme_id === 'pastel' ? 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' :
                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}></div>

        <div className="header-content">
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt={profile.display_name} className="profile-avatar" />
          ) : (
            <div className="profile-avatar-placeholder">
              {profile.display_name.charAt(0).toUpperCase()}
            </div>
          )}

          <div className="profile-info">
            <h1 className="profile-name">{profile.display_name}</h1>
            {profile.bio && <p className="profile-bio">{profile.bio}</p>}
            {profile.bot_username && (
              <p className="profile-username">@{profile.bot_username}</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            🛍️ Products
          </button>
          <button
            className={`tab ${activeTab === 'purchases' ? 'active' : ''}`}
            onClick={() => { setActiveTab('purchases'); loadPurchases(); }}
          >
            📦 My Purchases
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="content">
        {error && (
          <div className="alert error">
            <span>⚠️</span> {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="products-grid">
            {products.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📦</div>
                <h3>No Products Yet</h3>
                <p>Check back later for awesome items!</p>
              </div>
            ) : (
              products.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  purchasing={purchasing === product.id}
                  onPurchase={() => handlePurchase(product)}
                />
              ))
            )}
          </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div className="purchases-list">
            {purchases.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💫</div>
                <h3>No Purchases Yet</h3>
                <p>Support your favorite creator with Stars!</p>
              </div>
            ) : (
              purchases.map(purchase => (
                <div key={purchase.id} className="purchase-card">
                  {purchase.thumbnail_url && (
                    <img src={purchase.thumbnail_url} alt={purchase.title} className="purchase-image" />
                  )}
                  <div className="purchase-info">
                    <div className="purchase-type">{purchase.type}</div>
                    <h3 className="purchase-title">{purchase.title}</h3>
                    <div className="purchase-meta">
                      <span className="purchase-stars">⭐ {purchase.amount_stars}</span>
                      <span className="purchase-date">
                        {new Date(purchase.paid_at * 1000).toLocaleDateString()}
                      </span>
                    </div>
                    {purchase.resource_url && (
                      <a
                        href={purchase.resource_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn access-btn"
                      >
                        🔓 Access Content
                      </a>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Social Links */}
        {profile.social_links && profile.social_links.length > 0 && (
          <footer className="social-footer">
            <h3>Connect</h3>
            <div className="social-links">
              {profile.social_links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  style={{
                    background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                  }}
                >
                  {getSocialIcon(link.platform)} {link.platform}
                </a>
              ))}
            </div>
          </footer>
        )}
      </main>
    </div>
  );
}

function ProductCard({ product, purchasing, onPurchase }: {
  product: Product;
  purchasing: boolean;
  onPurchase: () => void;
}) {
  const progress = product.target_amount && product.current_amount !== undefined
    ? Math.min(100, (product.current_amount / product.target_amount) * 100)
    : 0;

  return (
    <div className={`product-card ${purchasing ? 'purchasing' : ''}`}>
      {product.thumbnail_url ? (
        <img src={product.thumbnail_url} alt={product.title} className="product-image" />
      ) : (
        <div className="product-image-placeholder">
          {product.type === 'wishlist' ? '🎯' : product.type === 'digital' ? '📦' : '🔗'}
        </div>
      )}

      <div className="product-content">
        <div className="product-header">
          <span className={`product-type product-type-${product.type}`}>
            {product.type === 'wishlist' ? '🎯 Wishlist' : product.type === 'digital' ? '📦 Digital' : '🔗 Link'}
          </span>
          <span className="product-price">⭐ {product.price_stars}</span>
        </div>

        <h3 className="product-title">{product.title}</h3>

        {product.description && (
          <p className="product-description">{product.description}</p>
        )}

        {product.type === 'wishlist' && product.target_amount && (
          <div className="wishlist-progress">
            <div className="progress-info">
              <span className="progress-current">{product.current_amount} ⭐</span>
              <span className="progress-target">{product.target_amount} ⭐</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="progress-percent">{Math.round(progress)}% funded</div>
          </div>
        )}

        <button
          className={`btn purchase-btn ${purchasing ? 'loading' : ''}`}
          onClick={onPurchase}
          disabled={purchasing}
        >
          {purchasing ? (
            <>
              <div className="btn-spinner"></div>
              Processing...
            </>
          ) : (
            <>💎 Buy with Stars</>
          )}
        </button>
      </div>
    </div>
  );
}

function getSocialIcon(platform: string): string {
  const icons: Record<string, string> = {
    instagram: '📸',
    x: '𝕏',
    twitter: '𝕏',
    youtube: '📺',
    tiktok: '🎵',
    website: '🌐',
    github: '💻',
    telegram: '✈️',
  };
  return icons[platform.toLowerCase()] || '🔗';
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<App />);
